"use client";

import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { MeasurementData, MeasurementUnit } from "@/lib/types/measurements";
import { convertUnit, formatMeasurement } from "@/lib/types/measurements";

interface MeasurementSilhouetteProps {
  data: MeasurementData;
  className?: string;
}

type MeasurementKey = keyof Omit<MeasurementData, "unit">;

const measurementLabels: Record<MeasurementKey, string> = {
  bust: "Bust",
  waist: "Waist",
  hip: "Hip",
  shoulder: "Shoulder",
  sleeveLength: "Sleeve Length",
  dressLength: "Dress Length",
};

export function MeasurementSilhouette({
  data,
  className,
}: MeasurementSilhouetteProps) {
  const [selectedMeasurement, setSelectedMeasurement] = useState<MeasurementKey | null>(null);
  const [unit, setUnit] = useState<MeasurementUnit>(data.unit);

  const getValue = (key: MeasurementKey): number => {
    return convertUnit(data[key], data.unit, unit);
  };

  const measurements: { key: MeasurementKey; y: number; lineY1: number; lineY2: number }[] = [
    { key: "shoulder", y: 80, lineY1: 75, lineY2: 85 },
    { key: "bust", y: 120, lineY1: 115, lineY2: 125 },
    { key: "waist", y: 180, lineY1: 175, lineY2: 185 },
    { key: "hip", y: 240, lineY1: 235, lineY2: 245 },
    { key: "sleeveLength", y: 150, lineY1: 80, lineY2: 150 },
    { key: "dressLength", y: 350, lineY1: 120, lineY2: 350 },
  ];

  return (
    <div className={cn("grid lg:grid-cols-2 gap-6", className)}>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Visual Guide</h3>
          <div className="flex gap-1 bg-muted p-1 rounded-lg">
            <Button
              variant={unit === "cm" ? "default" : "ghost"}
              size="sm"
              onClick={() => setUnit("cm")}
              className="text-xs h-7"
            >
              CM
            </Button>
            <Button
              variant={unit === "in" ? "default" : "ghost"}
              size="sm"
              onClick={() => setUnit("in")}
              className="text-xs h-7"
            >
              IN
            </Button>
          </div>
        </div>

        <svg
          viewBox="0 0 400 500"
          className="w-full max-w-md mx-auto"
          style={{ maxHeight: "600px" }}
        >
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="hsl(var(--primary))" />
            </marker>
          </defs>

          <g className="silhouette">
            <ellipse cx="200" cy="50" rx="25" ry="30" fill="hsl(var(--muted))" />

            <ellipse cx="200" cy="120" rx="50" ry="35" fill="hsl(var(--muted))" />

            <path
              d="M 200 90 L 200 350"
              stroke="hsl(var(--muted))"
              strokeWidth="60"
              fill="none"
              strokeLinecap="round"
            />

            <ellipse cx="200" cy="180" rx="45" ry="30" fill="hsl(var(--muted))" />

            <ellipse cx="200" cy="240" rx="60" ry="35" fill="hsl(var(--muted))" />

            <path
              d="M 150 100 L 110 150"
              stroke="hsl(var(--muted))"
              strokeWidth="20"
              strokeLinecap="round"
            />
            <path
              d="M 250 100 L 290 150"
              stroke="hsl(var(--muted))"
              strokeWidth="20"
              strokeLinecap="round"
            />

            <path
              d="M 170 350 L 170 450 M 230 350 L 230 450"
              stroke="hsl(var(--muted))"
              strokeWidth="25"
              strokeLinecap="round"
            />
          </g>

          {measurements.map(({ key, y, lineY1, lineY2 }) => {
            const isSelected = selectedMeasurement === key;
            const isHorizontal = key !== "sleeveLength" && key !== "dressLength";

            return (
              <g
                key={key}
                className="measurement-line cursor-pointer"
                onClick={() =>
                  setSelectedMeasurement(isSelected ? null : key)
                }
                onMouseEnter={() => setSelectedMeasurement(key)}
                onMouseLeave={() => setSelectedMeasurement(null)}
              >
                {isHorizontal ? (
                  <>
                    <line
                      x1="320"
                      y1={lineY1}
                      x2="320"
                      y2={lineY2}
                      stroke={isSelected ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"}
                      strokeWidth={isSelected ? "2" : "1.5"}
                      markerEnd="url(#arrowhead)"
                    />
                    <line
                      x1="80"
                      y1={lineY1}
                      x2="80"
                      y2={lineY2}
                      stroke={isSelected ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"}
                      strokeWidth={isSelected ? "2" : "1.5"}
                    />
                    <line
                      x1="80"
                      y1={y}
                      x2="320"
                      y2={y}
                      stroke={isSelected ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"}
                      strokeWidth={isSelected ? "2" : "1.5"}
                      strokeDasharray="4,4"
                    />
                    <text
                      x="340"
                      y={y + 5}
                      fill={isSelected ? "hsl(var(--primary))" : "hsl(var(--foreground))"}
                      fontSize="12"
                      fontWeight={isSelected ? "600" : "400"}
                    >
                      {formatMeasurement(getValue(key), unit)}
                    </text>
                  </>
                ) : (
                  <>
                    <line
                      x1="350"
                      y1={lineY1}
                      x2="350"
                      y2={lineY2}
                      stroke={isSelected ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"}
                      strokeWidth={isSelected ? "2" : "1.5"}
                      markerEnd="url(#arrowhead)"
                    />
                    <text
                      x="360"
                      y={(lineY1 + lineY2) / 2 + 5}
                      fill={isSelected ? "hsl(var(--primary))" : "hsl(var(--foreground))"}
                      fontSize="12"
                      fontWeight={isSelected ? "600" : "400"}
                    >
                      {formatMeasurement(getValue(key), unit)}
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </svg>

        {selectedMeasurement && (
          <div className="mt-4 p-4 bg-accent rounded-lg">
            <p className="text-sm font-semibold text-accent-foreground">
              {measurementLabels[selectedMeasurement]}
            </p>
            <p className="text-2xl font-bold text-primary">
              {formatMeasurement(getValue(selectedMeasurement), unit)}
            </p>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">All Measurements</h3>
        <div className="space-y-3">
          {Object.entries(measurementLabels).map(([key, label]) => {
            const measurementKey = key as MeasurementKey;
            const isSelected = selectedMeasurement === measurementKey;

            return (
              <button
                key={key}
                onClick={() =>
                  setSelectedMeasurement(isSelected ? null : measurementKey)
                }
                className={cn(
                  "w-full flex items-center justify-between p-3 rounded-lg transition-colors text-left",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted hover:bg-muted/80"
                )}
              >
                <span className="font-medium">{label}</span>
                <span className="text-sm font-semibold">
                  {formatMeasurement(getValue(measurementKey), unit)}
                </span>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
